import { createAsyncThunk } from '@reduxjs/toolkit';

import { AdminApiPaths } from '../../../api/APIPaths';
import APIDataProvider, {
  TApiPaginationResponse,
  TApiResponse,
} from '../../../api/ApiDataProvider';
import {
  ICoursesForSubscriptions,
  ICourseStudent,
  TCourseStudentPricingType,
} from '.';

export type TGetCourseStudentsAsAdmin = {
  course: string;
  limit: number;
  offset: number;
  status?: ICourseStudent['status'];
};

export type TCreateCourseStudent = {
  course: string;
  student: string;
  startDate?: string;
  endDate?: string;
  pricingType?: TCourseStudentPricingType;
};

export type TUpdateCourseStudent = {
  id: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  pricingType?: TCourseStudentPricingType;
};

const apiDataProvider = new APIDataProvider();

const getCourseStudentsAsAdminModule = async ({
  course,
  status,
  limit,
  offset,
}: TGetCourseStudentsAsAdmin) => {
  let path = `${AdminApiPaths.courseStudents}?limit=${limit}&offset=${offset}`;

  if (course) {
    path += `&course=${course}`;
  }

  if (status) {
    path += `&status=${status}`;
  }

  const response = await apiDataProvider.get<
    TApiResponse<TApiPaginationResponse<ICourseStudent>>
  >(path);

  return response.data;
};

export const getCourseStudentsAsAdmin = createAsyncThunk(
  '/admin/course-students/list',
  getCourseStudentsAsAdminModule
);

export const createCourseStudent = createAsyncThunk(
  '/admin/course-students/create',
  async (data: TCreateCourseStudent) => {
    const path = `${AdminApiPaths.courseStudents}`;

    const response = await apiDataProvider.post<TApiResponse<ICourseStudent>>(
      path,
      data
    );

    return response.data;
  }
);

export const deleteCourseStudent = createAsyncThunk(
  '/admin/course-students/delete',
  async (id: string) => {
    const path = `${AdminApiPaths.courseStudents}/${id}`;

    const response = await apiDataProvider.delete<TApiResponse<ICourseStudent>>(
      path
    );

    return response.data;
  }
);

export const updateCourseStudent = createAsyncThunk(
  '/admin/course-students/update',
  async ({ id, ...data }: TUpdateCourseStudent) => {
    const path = `${AdminApiPaths.courseStudents}/${id}`;

    const response = await apiDataProvider.put<TApiResponse<ICourseStudent>>(
      path,
      data
    );

    return response.data;
  }
);

export const getCoursesByUserAsAdmin = createAsyncThunk(
  '/admin/course-students/getCoursesByUser',
  async ({
    userId,
    deviceTimezone,
  }: {
    userId: string;
    deviceTimezone?: string;
  }) => {
    let path = `${AdminApiPaths.courseStudents}/user/${userId}?userId=${userId}`;

    if (deviceTimezone) {
      path += `&deviceTimezone=${deviceTimezone};`;
    }

    const response = await apiDataProvider.get<
      TApiResponse<ICoursesForSubscriptions>
    >(path);

    return response.data;
  }
);
